// Button Size Classes - Mobile-first: small on mobile, scale up gradually
export const buttonSizes = {
  sm: 'p-2 text-xs sm:text-sm md:text-base',
  md: 'p-4 text-xs sm:text-sm md:text-base lg:px-5',
  lg: 'p-5 text-sm sm:text-base md:text-lg lg:px-6',
} as const

// Button Color Classes - Suladio brand primary buttons
export const buttonColors = {
  primary: 'bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl text-white',
  secondary: 'bg-secondary text-white hover:bg-secondary/80',
  outline: 'border border bg-background hover:bg-muted text-foreground',
  ghost: 'hover:bg-muted text-foreground',
  light:
    'bg-background text-foreground border border-border hover:bg-muted shadow-lg hover:shadow-xl',
  dark: 'bg-foreground text-background hover:bg-foreground/90 shadow-lg hover:shadow-xl',
} as const

// Base Button Classes
export const baseButtonClasses =
  'flex items-center gap-1 sm:gap-2 justify-center font-medium rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-all duration-300'

// Card Classes
export const cardClasses = {
  base: 'bg-card rounded-xl sm:rounded-2xl border border-border shadow-lg hover:shadow-xl transition-all duration-300',
  glass: 'bg-white/10 backdrop-blur-md border border-white/20 rounded-xl sm:rounded-2xl',
  glassDark: 'bg-black/10 backdrop-blur-md border border-white/10 rounded-xl sm:rounded-2xl',
} as const

// Typography Classes - Much smaller on mobile
export const typographyClasses = {
  h1: 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-5xl font-bold tracking-tight',
  h2: 'text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-semibold tracking-tight',
  h3: 'text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-semibold',
  h4: 'text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-medium',
  h5: 'text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-medium',
  h6: 'text-xs sm:text-sm md:text-base lg:text-lg font-medium',
  gradient: 'bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent',
  display: 'text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight',
} as const

// Spacing Classes - Tighter on mobile
export const spacingClasses = {
  section: 'px-3 py-8 sm:px-4 sm:py-12 md:px-6 md:py-16 lg:px-24',
  hero: 'px-3 py-12 sm:px-4 sm:py-16 md:px-6 md:py-20 lg:px-24',
  container: 'max-w-7xl mx-auto',
  containerSm: 'max-w-4xl mx-auto',
  containerMd: 'max-w-6xl mx-auto',
} as const

// Animation Classes
export const animationClasses = {
  fadeIn: 'animate-fade-in',
  slideUp: 'animate-slide-up',
  scaleIn: 'animate-scale-in',
  bounceGentle: 'animate-bounce-gentle',
} as const

// Input Classes
export const inputClasses = {
  base: 'w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background transition-colors',
  error: 'border-destructive focus:ring-destructive',
  success: 'border-success focus:ring-success',
} as const

// Badge Classes - Much smaller on mobile
export const badgeClasses = {
  primary:
    'bg-primary text-primary-foreground px-1.5 py-0.5 sm:px-2 sm:py-0.5 md:px-4 md:py-1 rounded-full text-xs font-medium shadow-lg',
  success:
    'bg-success text-success-foreground px-1.5 py-0.5 sm:px-2 sm:py-0.5 md:px-4 md:py-1 rounded-full text-xs font-medium',
  warning:
    'bg-warning text-warning-foreground px-1.5 py-0.5 sm:px-2 sm:py-0.5 md:px-4 md:py-1 rounded-full text-xs font-medium',
  error:
    'bg-destructive text-destructive-foreground px-1.5 py-0.5 sm:px-2 sm:py-0.5 md:px-4 md:py-1 rounded-full text-xs font-medium',
  gradient:
    'bg-gradient-to-r from-orange-500 to-red-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-0.5 md:px-4 md:py-1 rounded-full text-xs font-medium shadow-lg',
} as const

// Icon Container Classes - Much smaller on mobile
export const iconContainerClasses = {
  sm: 'w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-primary/10 rounded-lg flex items-center justify-center',
  md: 'w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 bg-primary/10 rounded-lg sm:rounded-xl flex items-center justify-center',
  lg: 'w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-primary/10 rounded-xl sm:rounded-2xl flex items-center justify-center',
} as const

// Grid Classes - Tighter spacing on mobile
export const gridClasses = {
  features: 'grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8',
  pricing: 'grid gap-4 sm:gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto',
  cards: 'grid gap-3 sm:gap-4 md:gap-6 md:grid-cols-2 lg:grid-cols-3',
} as const

// Text Colors
export const textColors = {
  primary: 'text-foreground',
  secondary: 'text-muted-foreground',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-destructive',
} as const

// Background Colors
export const backgroundColors = {
  primary: 'bg-background',
  secondary: 'bg-card',
  muted: 'bg-muted',
  primarySubtle: 'bg-primary/10',
  successSubtle: 'bg-success/20',
  warningSubtle: 'bg-warning/20',
  errorSubtle: 'bg-destructive/20',
} as const

// Border Classes
export const borderClasses = {
  base: 'border border-border',
  primary: 'border border-primary',
  success: 'border border-success',
  warning: 'border border-warning',
  error: 'border border-destructive',
} as const

// Shadow Classes
export const shadowClasses = {
  sm: 'shadow-sm',
  base: 'shadow',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  glow: 'shadow-glow',
  glowLg: 'shadow-glow-lg',
} as const

// D.R.I.V.E. Colors
export const driveColors: { [key: string]: string } = {
  drive: '#029C90',
  relator: '#A765A9',
  intellectual: '#F78F1F',
  validator: '#0B4C80',
  executive: '#575757',
} as const

// Type definitions for better TypeScript support
export type ButtonSize = keyof typeof buttonSizes
export type ButtonColor = keyof typeof buttonColors
export type TypographySize = keyof typeof typographyClasses
export type BadgeVariant = keyof typeof badgeClasses
export type IconSize = keyof typeof iconContainerClasses
export type TextColor = keyof typeof textColors
export type BackgroundColor = keyof typeof backgroundColors
export type BorderVariant = keyof typeof borderClasses
export type ShadowSize = keyof typeof shadowClasses
export type DriveColor = keyof typeof driveColors
